import React, { useState, useEffect, useMemo } from 'react';
import '../styles/MessagesTab.css';
import ConfirmDialog from './ConfirmDialog';
import DateFilterCalendar from './DateFilterCalendar';
import {
  ClassSectionFilters,
  SubjectColumn,
  itemMatchesClass,
  itemMatchesAssignmentWord,
  itemsInClass
} from './ClassFolderSearch';
import { RecheckDecisionBody } from '../utils/recheckMessage';

function messageDateKey(value) {
  if (!value) return '';
  if (typeof value === 'string') {
    const match = value.match(/^(\d{4}-\d{2}-\d{2})/);
    if (match) return match[1];
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function splitClassSubject(title) {
  const raw = String(title || '').trim();
  const match = raw.match(/^(.*?)\s+-\s+(.+)$/);
  if (!match) return { className: raw, subject: '' };
  return { className: match[1].trim(), subject: match[2].trim() };
}

function assignmentFromText(text) {
  const word = String(text || '').match(/\b(paper|test)\b/i);
  return word ? word[1] : '';
}

function folderFieldsFromMessage(message) {
  const first = String(message && message.message_text || '').split(/\n/)[0].trim();
  const regrade = first.match(/^Re-Grade\s+\d+\s*·\s*(.+)$/i);
  if (regrade) {
    const split = splitClassSubject(regrade[1]);
    return { class: split.className, subject: split.subject, assignment_name: assignmentFromText(first) };
  }
  const recheck = first.match(/\(([^)]+)\)\s*·\s*(.+)$/);
  if (recheck) {
    const split = splitClassSubject(recheck[1]);
    const subject = recheck[2].replace(/\s*(?:->|—|–)\s*.*$/, '').trim();
    return {
      class: split.className,
      subject: subject || split.subject,
      assignment_name: assignmentFromText(first)
    };
  }
  return { class: '', subject: '', assignment_name: assignmentFromText(first) };
}

function TeacherColumn({ teachers, selected, onSelect }) {
  return (
    <div className="msg-teachers">
      <span className="msg-teachers-label">Teachers</span>
      {teachers.length === 0 ? (
        <span className="msg-teachers-empty">No teachers on these messages.</span>
      ) : teachers.map((teacher) => {
        const active = selected === teacher.key;
        const count = teacher.count || 0;
        return (
          <button
            key={teacher.key}
            type="button"
            onClick={() => onSelect(active ? '' : teacher.key)}
            title={active ? 'Show every teacher' : `Show messages sent to ${teacher.label}`}
            className={`msg-teacher${active ? ' is-active' : ''}${count ? ' has-count' : ' is-empty'}`}
          >
            <span className="msg-teacher-name">{teacher.label}</span>
            <span className="msg-teacher-count">{count}</span>
          </button>
        );
      })}
    </div>
  );
}

const API_BASE_URL = window.API_BASE_URL;

export default function MessagesTab({ currentUser }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [confirm, setConfirm] = useState({ show: false, text: '', onConfirm: null });
  const [showDone, setShowDone] = useState(false);
  const [savingDoneId, setSavingDoneId] = useState(null);
  const [teacherFilter, setTeacherFilter] = useState('');
  const [gradeFilter, setGradeFilter] = useState(null);
  const [sectionFilter, setSectionFilter] = useState(null);
  const [subjectPick, setSubjectPick] = useState(null);
  const [assignmentWord, setAssignmentWord] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [rangeFrom, setRangeFrom] = useState('');
  const [rangeTo, setRangeTo] = useState('');

  useEffect(() => {
    fetchMessages(showDone);
  }, [showDone]);

  useEffect(() => {
    markMessagesRead();
  }, []);

  useEffect(() => {
    if (!success) return;
    const timer = setTimeout(() => setSuccess(''), 3000);
    return () => clearTimeout(timer);
  }, [success]);

  const markMessagesRead = async () => {
    try {
      const token = localStorage.getItem('authToken');
      await fetch(`${API_BASE_URL}/messages/mark-read`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (_) { /* non-critical */ }
  };

  const fetchMessages = async (includeDone = showDone) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(
        `${API_BASE_URL}/messages${includeDone ? '?includeDone=1' : ''}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      const data = await response.json();
      if (data.success) {
        setMessages(data.messages);
        setError('');
      } else {
        setError('Failed to load messages');
      }
    } catch (err) {
      setError('Failed to load messages');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const showConfirm = (text, callback) => setConfirm({ show: true, text, onConfirm: callback });
  const handleConfirm = () => {
    confirm.onConfirm();
    setConfirm({ show: false, text: '', onConfirm: null });
  };
  const handleCancel = () => setConfirm({ show: false, text: '', onConfirm: null });

  const handleSetDone = async (messageId, done) => {
    setSavingDoneId(messageId);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/messages/${messageId}/done`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ done })
      });

      const data = await response.json();

      if (data.success) {
        setError('');
        setSuccess(done ? 'Marked as done' : 'Message restored');
        if (done && !showDone) {
          setMessages((prev) => prev.filter((m) => m.id !== messageId));
        } else {
          setMessages((prev) => prev.map((m) => (
            m.id === messageId ? { ...m, is_done: done ? 1 : 0 } : m
          )));
        }
      } else {
        setError(data.message || 'Failed to update message');
      }
    } catch (err) {
      setError('Failed to update message');
      console.error(err);
    } finally {
      setSavingDoneId(null);
    }
  };

  const handleDeleteMessage = async (messageId) => {
    showConfirm('Are you sure you want to delete this message?', async () => {
      try {
        const token = localStorage.getItem('authToken');
        const response = await fetch(`${API_BASE_URL}/messages/${messageId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        const data = await response.json();

        if (data.success) {
          setSuccess('Message deleted successfully');
          fetchMessages();
        } else {
          setError(data.message || 'Failed to delete message');
        }
      } catch (err) {
        setError('Failed to delete message');
        console.error(err);
      }
    });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  };

  // Recipients mark notices as done instead of deleting them, so the delete
  // button stays with the author / administrators.
  const canDeleteMessage = (message) => {
    return (
      currentUser.role === 'administrator'
      || message.user_id === currentUser.id
    );
  };

  const isPrivateNotice = (message) =>
    message.recipient_user_id != null || message.message_type === 'recheck_decision';

  // Only the teacher the notice was sent to can mark it handled.
  const canMarkDone = (message) => {
    if (typeof message.can_mark_done === 'boolean') return message.can_mark_done;
    return message.recipient_user_id != null && message.recipient_user_id === currentUser.id;
  };

  const noticeLabel = (message) => {
    if (message.message_type === 'recheck_decision') return 'Re-check update';
    if (message.message_type === 'regrade_reply') return 'Re-grade reply';
    if (message.recipient_user_id != null) return 'Private notice';
    return null;
  };

  const personName = (fullName, username) => {
    const name = String(fullName || '').trim();
    const user = String(username || '').trim();
    return name || user;
  };

  const catalog = useMemo(() => messages.map((message) => {
    const fields = folderFieldsFromMessage(message);
    const hasReceiver = message.recipient_user_id != null;
    return {
      ...message,
      ...fields,
      teacherKey: hasReceiver ? String(message.recipient_user_id) : '',
      teacherLabel: hasReceiver
        ? personName(message.recipient_full_name, message.recipient_username)
        : ''
    };
  }), [messages]);

  const teachers = useMemo(() => {
    const byKey = new Map();
    catalog.forEach((message) => {
      if (!message.teacherKey || !message.teacherLabel) return;
      const related = itemMatchesClass(message, gradeFilter, sectionFilter, subjectPick)
        && itemMatchesAssignmentWord(message, assignmentWord);
      const prev = byKey.get(message.teacherKey);
      if (!prev) {
        byKey.set(message.teacherKey, {
          key: message.teacherKey,
          label: message.teacherLabel,
          count: related ? 1 : 0
        });
      } else if (related) {
        prev.count += 1;
      }
    });
    return [...byKey.values()].sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
  }, [catalog, gradeFilter, sectionFilter, subjectPick, assignmentWord]);

  const afterTeacher = teacherFilter
    ? catalog.filter((message) => message.teacherKey === teacherFilter)
    : catalog;

  const calendarItems = afterTeacher.filter((message) => (
    itemMatchesClass(message, gradeFilter, sectionFilter, subjectPick)
    && itemMatchesAssignmentWord(message, assignmentWord)
  ));

  const formatDayLabel = (key) => {
    if (!key) return '';
    return new Date(
      Number(key.slice(0, 4)),
      Number(key.slice(5, 7)) - 1,
      Number(key.slice(8, 10))
    ).toLocaleDateString('en-PK', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const rangeActive = !!(rangeFrom || rangeTo);
  const visible = calendarItems.filter((message) => {
    const key = messageDateKey(message.created_at);
    if (rangeActive) {
      if (!key) return false;
      const from = rangeFrom && rangeTo && rangeFrom > rangeTo ? rangeTo : rangeFrom;
      const to = rangeFrom && rangeTo && rangeFrom > rangeTo ? rangeFrom : rangeTo;
      if (from && key < from) return false;
      if (to && key > to) return false;
      return true;
    }
    if (selectedDate) return key === selectedDate;
    return true;
  });

  const dayLabel = rangeActive
    ? [formatDayLabel(rangeFrom), formatDayLabel(rangeTo)].filter(Boolean).join(' – ')
    : formatDayLabel(selectedDate);

  return (
    <div className="messages-tab">
      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <DateFilterCalendar
        items={calendarItems}
        dateField="created_at"
        selectedDate={selectedDate}
        onDateChange={(day) => {
          setRangeFrom('');
          setRangeTo('');
          setSelectedDate(day || '');
        }}
        itemLabelSingular="Message"
        itemLabelPlural="Messages"
        title="Sent"
        columns={15}
        windowDays={15}
        showCount
        compact
        headerAside={(
          <div className="messages-header-actions">
            <button
              type="button"
              className={`btn-toggle-done${showDone ? ' is-active' : ''}`}
              onClick={() => setShowDone((prev) => !prev)}
              disabled={loading}
            >
              <i className={`fas ${showDone ? 'fa-eye-slash' : 'fa-eye'}`}></i>
              {showDone ? 'Hide done' : 'Show done'}
            </button>
            <button
              type="button"
              className="btn-refresh"
              onClick={() => fetchMessages()}
              disabled={loading}
              title="Refresh"
            >
              <i className={`fas fa-sync-alt${loading ? ' fa-spin' : ''}`}></i>
            </button>
          </div>
        )}
      />

      <div className="messages-board">
        <div className="messages-rail">
          <TeacherColumn
            teachers={teachers}
            selected={teacherFilter}
            onSelect={setTeacherFilter}
          />
        </div>
        <div className="messages-main">
          <ClassSectionFilters
            items={afterTeacher}
            itemNoun="messages"
            gradeFilter={gradeFilter}
            sectionFilter={sectionFilter}
            subjectFilter={subjectPick}
            assignmentWord={assignmentWord}
            onAssignmentWord={setAssignmentWord}
            showCounts
            appearance="inbox"
            onGrade={(grade) => {
              setSubjectPick(null);
              setSectionFilter(null);
              setGradeFilter(gradeFilter === grade ? null : grade);
            }}
            onSection={(section) => {
              const next = sectionFilter === section ? null : section;
              setSectionFilter(next);
              if (subjectPick && !itemsInClass(afterTeacher, gradeFilter, next).some((message) => (
                String(message.subject || '').trim() === subjectPick
              ))) {
                setSubjectPick(null);
              }
            }}
          />
      <div className="messages-list">
        <div className="messages-list-head">
          <h3>
            {showDone ? 'All Messages' : 'Open Messages'}
            {dayLabel ? ` · ${dayLabel}` : ''}
            {' '}({visible.length})
          </h3>
          <label className="messages-date-filter">
            <span>Date range</span>
            <input
              type="date"
              value={rangeFrom}
              aria-label="Range start"
              onChange={(event) => {
                setSelectedDate('');
                setRangeFrom(event.target.value);
              }}
            />
            <span className="messages-date-to">to</span>
            <input
              type="date"
              value={rangeTo}
              aria-label="Range end"
              onChange={(event) => {
                setSelectedDate('');
                setRangeTo(event.target.value);
              }}
            />
          </label>
          <label className="messages-date-filter">
            <span>Any date</span>
            <input
              type="date"
              value={rangeActive ? '' : selectedDate}
              aria-label="Select any date"
              onChange={(event) => {
                setRangeFrom('');
                setRangeTo('');
                setSelectedDate(event.target.value);
              }}
            />
          </label>
        </div>
        {catalog.length === 0 ? (
          <div className="no-messages">
            <p>{showDone ? 'No messages yet.' : 'Nothing left to handle.'}</p>
          </div>
        ) : visible.length === 0 ? (
          <div className="no-messages">
            <p>No messages for this choice.</p>
          </div>
        ) : (
          <div className="messages-container">
            {visible.map((message) => {
              const label = noticeLabel(message);
              const isRecheck = message.message_type === 'recheck_decision';
              const isDone = Boolean(message.is_done);
              const hasReceiver = message.recipient_user_id != null;
              const senderName = personName(message.full_name, message.username);
              const receiverName = hasReceiver
                ? personName(message.recipient_full_name, message.recipient_username)
                : '';
              return (
                <div
                  key={message.id}
                  className={`message-card${isPrivateNotice(message) ? ' message-card--notice' : ''}${!message.is_read && isPrivateNotice(message) ? ' message-card--unread' : ''}${isDone ? ' message-card--done' : ''}`}
                >
                  <div className="message-header-row">
                    <div className="message-author">
                      {hasReceiver ? (
                        <div className="message-parties">
                          <span className="message-party">
                            <span className="message-party-label">From</span>
                            <span className="author-name">{senderName || '—'}</span>
                          </span>
                          <span className="message-party">
                            <span className="message-party-label">To</span>
                            <span className="author-name">{receiverName || '—'}</span>
                          </span>
                        </div>
                      ) : (
                        <span className="author-name">{senderName}</span>
                      )}
                    </div>
                    <div className="message-meta">
                      <span className="message-date">{formatDate(message.created_at)}</span>
                      {canMarkDone(message) && (
                        <button
                          type="button"
                          className={`btn-mark-done${isDone ? ' btn-mark-done--undo' : ''}`}
                          onClick={() => handleSetDone(message.id, !isDone)}
                          disabled={savingDoneId === message.id}
                          title={isDone
                            ? 'Move this notice back to your list'
                            : 'Marks already updated on Edupage — hide this notice'}
                        >
                          <i className={`fas ${isDone ? 'fa-rotate-left' : 'fa-check'}`}></i>
                          {isDone ? 'Undo' : 'Mark as done'}
                        </button>
                      )}
                      {canDeleteMessage(message) && (
                        <button
                          className="btn-delete-message"
                          onClick={() => handleDeleteMessage(message.id)}
                          title="Delete message"
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      )}
                    </div>
                    <div className="message-badges">
                      {message.role && (
                        <span className={`author-role role-${message.role}`}>{message.role}</span>
                      )}
                      {label && <span className="message-type-badge">{label}</span>}
                      {isDone && <span className="message-done-badge">Done</span>}
                    </div>
                  </div>
                  <div className="message-content">
                    {isRecheck
                      ? <RecheckDecisionBody text={message.message_text} />
                      : message.message_text}
                    {message.audio_url && (
                      <audio className="message-audio" controls src={message.audio_url} />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
        </div>
        <SubjectColumn
          items={afterTeacher}
          gradeFilter={gradeFilter}
          sectionFilter={sectionFilter}
          subjectFilter={subjectPick}
          showCounts
          appearance="inbox"
          onSubject={(subject) => setSubjectPick(subjectPick === subject ? null : subject)}
        />
      </div>

      <ConfirmDialog
        confirm={confirm}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </div>
  );
}
